import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { Meal, CreateMealDto, UpdateMealDto, AssignMealToDateDto, MealFilters, MealDateRangeParams, MealDateParams } from '../types/meal.types';
import mealService from '../services/meal/meal.service';
import { handleApiError } from '../utils/errorHandler';

export const useMeals = () => {
  const [allMeals, setAllMeals] = useState<Meal[]>([]);
  const [filteredMeals, setFilteredMeals] = useState<Meal[]>([]);
  const [selectedMealType, setSelectedMealType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Yemekleri filtrelemek için useEffect
  useEffect(() => {
    if (allMeals) {
      let filtered = [...allMeals];

      // Yemek türü filtreleme
      if (selectedMealType !== 'all') {
        filtered = filtered.filter(meal => meal.type === selectedMealType);
      }

      // Arama terimi filtreleme
      if (searchTerm) {
        filtered = filtered.filter(meal =>
          meal.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (meal.description && meal.description.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }

      // Tarih aralığı filtreleme
      if (dateRange.startDate && dateRange.endDate) {
        filtered = filtered.filter(meal => {
          if (!meal.dates || meal.dates.length === 0) return false;
          const start = new Date(dateRange.startDate!);
          const end = new Date(dateRange.endDate!);
          return meal.dates.some(mealDate => {
            const date = new Date(mealDate.date);
            return date >= start && date <= end;
          });
        });
      }

      setFilteredMeals(filtered);
    }
  }, [allMeals, selectedMealType, searchTerm, dateRange]);

  // Tüm yemekleri getir
  const fetchMeals = useCallback(async (params?: MealDateRangeParams, branchId?: number) => {
    setLoading(true);
    setError(null);
    try {
      let meals: Meal[];
      if (params) {
        meals = await mealService.getMealsByWeek(params);
      } else {
        // Varsayılan olarak tüm yemekleri getir
        meals = await mealService.getAllMeals(branchId);
      }
      setAllMeals(meals);
    } catch (err: any) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Yemek türü değişikliği
  const handleMealTypeChange = (mealType: string) => {
    setSelectedMealType(mealType);
  };

  // Arama değişikliği
  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
  };

  // Tarih aralığı değişikliği
  const handleDateRangeChange = (startDate?: string, endDate?: string) => {
    setDateRange({ startDate, endDate });
  };

  // Filtreleri sıfırla
  const resetFilters = () => {
    setSelectedMealType('all');
    setSearchTerm('');
    setDateRange({});
  };

  // Belirli bir tarihteki yemeği getir
  const getMealByDate = useCallback(async (params: MealDateParams) => {
    try {
      const meals = await mealService.getMealsByDate(params);
      return meals.length > 0 ? meals[0] : null;
    } catch (err: any) {
      handleApiError(err);
      return null;
    }
  }, []);

  // Bugünkü yemekleri getir
  const getTodaysMeals = useCallback(async (branchId: number) => {
    try {
      const meals = await mealService.getTodaysMeals(branchId);
      return meals;
    } catch (err: any) {
      handleApiError(err);
      return [];
    }
  }, []);

  // Haftalık yemekleri getir
  const getWeeklyMeals = useCallback(async (params: MealDateRangeParams) => {
    try {

      if (!params.branchId) {
        throw new Error('Branch ID is required for getWeeklyMeals');
      }

      const meals = await mealService.getMealsByWeek(params);

      return meals;
    } catch (err: any) {
      handleApiError(err);
      return [];
    }
  }, []);

  return {
    // State
    meals: allMeals,
    filteredMeals,
    loading,
    error,
    selectedMealType,
    searchTerm,
    dateRange,

    // Actions
    fetchMeals,
    handleMealTypeChange,
    handleSearchChange,
    handleDateRangeChange,
    resetFilters,
    getMealByDate,
    getTodaysMeals,
    getWeeklyMeals
  };
};

export const useMealActions = () => {
  const [loading, setLoading] = useState<boolean>(false);

  const createMeal = useCallback(async (data: CreateMealDto): Promise<Meal | null> => {
    setLoading(true);
    try {
      const meal = await mealService.createMeal(data);
      message.success("Yemek başarıyla oluşturuldu");
      return meal;
    } catch (err: any) {
      handleApiError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateMeal = useCallback(async (id: number, data: UpdateMealDto): Promise<Meal | null> => {
    setLoading(true);
    try {
      const meal = await mealService.updateMeal(id, data);
      message.success("Yemek başarıyla güncellendi");
      return meal;
    } catch (err: any) {
      handleApiError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteMeal = useCallback(async (id: number): Promise<boolean> => {
    setLoading(true);
    try {
      await mealService.deleteMeal(id);
      message.success("Yemek başarıyla silindi");
      return true;
    } catch (err: any) {
      handleApiError(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const assignMealToDate = useCallback(async (id: number, data: AssignMealToDateDto): Promise<Meal | null> => {
    setLoading(true);
    try {
      const meal = await mealService.assignMealToDate(id, data);
      message.success("Yemek başarıyla güne atandı");
      return meal;
    } catch (err: any) {
      handleApiError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const removeMealFromDate = useCallback(async (id: number, date: string): Promise<boolean> => {
    setLoading(true);
    try {
      await mealService.removeMealFromDate(id, date);
      message.success("Yemek başarıyla günden kaldırıldı");
      return true;
    } catch (err: any) {
      handleApiError(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateMealStatus = useCallback(async (id: number, isActive: boolean): Promise<boolean> => {
    setLoading(true);
    try {
      await mealService.updateMealStatus(id, isActive);
      message.success(`Yemek ${isActive ? 'aktif' : 'pasif'} duruma getirildi`);
      return true;
    } catch (err: any) {
      handleApiError(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    createMeal,
    updateMeal,
    deleteMeal,
    assignMealToDate,
    removeMealFromDate,
    updateMealStatus
  };
};